import { resolveLoginClientId } from '../auth/appRegistration';

// Environment validation and configuration management
export interface EnvironmentConfig {
  // Required variables
  azureClientId: string;
  apiAppIdUri: string;
  
  // Optional variables with defaults
  azureTenantId: string;
  azureAuthority: string;
  apiBaseUrl: string;
  teamsPresenceClientId: string;
  azureMapsKey?: string;
  
  // Feature flags
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  enableAnalytics: boolean;
  enableMonitoring: boolean;
  enablePresence: boolean;
  
  // Environment info
  isDevelopment: boolean;
  isProduction: boolean;
  buildVersion?: string;
  deploymentEnvironment: 'development' | 'staging' | 'production' | 'testing';
}

/**
 * Validates that all required environment variables are present and valid
 */
export function validateEnvironment(): EnvironmentConfig {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Helper to get and validate required environment variable
  const getRequiredEnv = (key: string, validation?: (value: string) => boolean): string => {
    const value = import.meta.env[key];
    if (!value || value.includes('<') || value.trim() === '') {
      errors.push(`Missing or invalid required environment variable: ${key}`);
      return '';
    }
    
    if (validation && !validation(value)) {
      errors.push(`Invalid format for environment variable: ${key}`);
      return '';
    }
    
    return value;
  };

  // Helper to get optional environment variable with default
  const getOptionalEnv = (key: string, defaultValue: string): string => {
    const value = import.meta.env[key];
    if (!value || value.includes('<') || value.trim() === '') {
      return defaultValue;
    }
    return value;
  };

  // Helper to get boolean environment variable
  const getBooleanEnv = (key: string, defaultValue: boolean): boolean => {
    const value = import.meta.env[key];
    if (value === undefined || value === '') {
      return defaultValue;
    }
    return value.toLowerCase() === 'true';
  };

  // Validate URL format
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Validate GUID format
  const isValidGuid = (guid: string): boolean => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  };

  // Required environment variables
  const azureClientId = (() => {
    try {
      return resolveLoginClientId(
        getOptionalEnv('VITE_EXOBE_ID', ''),
        getOptionalEnv('VITE_AZURE_CLIENT_ID', '')
      );
    } catch (error) {
      errors.push(error instanceof Error ? error.message : 'Failed to resolve login client ID.');
      return '';
    }
  })();
  const apiAppIdUri = getRequiredEnv('VITE_API_APP_ID_URI', (value) => {
    // Accept both formats:
    // 1. api://{guid}
    // 2. api://{domain}/{guid} (Teams SSO format)
    if (!value.startsWith('api://')) return false;
    
    const withoutPrefix = value.replace('api://', '');
    
    // Check if it's just a GUID
    if (isValidGuid(withoutPrefix)) return true;
    
    // Check if it's domain/GUID format
    const parts = withoutPrefix.split('/');
    if (parts.length === 2 && parts[0] && parts[0].length > 0 && parts[1] && isValidGuid(parts[1])) {
      return true;
    }
    
    return false;
  });

  // Optional environment variables with validation
  const azureTenantId = getOptionalEnv('VITE_AZURE_TENANT_ID', 'common');
  const azureAuthority = getOptionalEnv(
    'VITE_AZURE_AUTHORITY', 
    `https://login.microsoftonline.com/${azureTenantId}`
  );
  const apiBaseUrl = getOptionalEnv(
    'VITE_API_BASE_URL',
    import.meta.env.DEV ? 'http://localhost:7071' : ''
  );

  // Validate URLs
  if (apiBaseUrl && !isValidUrl(apiBaseUrl)) {
    errors.push('Invalid VITE_API_BASE_URL format');
  }
  if (!isValidUrl(azureAuthority)) {
    errors.push('Invalid VITE_AZURE_AUTHORITY format');
  }

  // Additional app registrations
  const teamsPresenceClientId = getOptionalEnv('VITE_TEAMS_PRESENCE_CLIENT_ID', '');

  // Validate additional client IDs if provided
  if (import.meta.env.VITE_TEAMS_PRESENCE_CLIENT_ID && !isValidGuid(teamsPresenceClientId)) {
    errors.push('Invalid VITE_TEAMS_PRESENCE_CLIENT_ID format');
  }

  // Feature flags and settings
  const logLevel = getOptionalEnv('VITE_LOG_LEVEL', 'info') as 'debug' | 'info' | 'warn' | 'error';
  const validLogLevels = ['debug', 'info', 'warn', 'error'];
  if (!validLogLevels.includes(logLevel)) {
    warnings.push(`Invalid VITE_LOG_LEVEL "${logLevel}", using "info"`);
  }

  // Environment detection
  const isDevelopment = import.meta.env.DEV;
  const isProduction = import.meta.env.PROD;
  const deploymentEnvironment = getOptionalEnv('VITE_DEPLOYMENT_ENV', 
    isDevelopment ? 'development' : 'production'
  ) as 'development' | 'staging' | 'production' | 'testing';

  // Development-specific warnings
  if (isDevelopment) {
    // Development mode - no special warnings needed
  } else {
    // Production-specific validations
    if (apiBaseUrl.includes('localhost')) {
      errors.push('Production build cannot use localhost API URL');
    }
  }

  // Report validation results
  if (warnings.length > 0) {
    console.warn('Environment validation warnings:', warnings);
  }

  if (errors.length > 0) {
    const errorMessage = `Environment validation failed:\n${errors.join('\n')}`;
    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  const config: EnvironmentConfig = {
    // Required
    azureClientId,
    apiAppIdUri,
    
    // Optional with defaults
    azureTenantId,
    azureAuthority,
    apiBaseUrl,
    teamsPresenceClientId,
    azureMapsKey: getOptionalEnv('VITE_AZURE_MAPS_KEY', ''),
    
    // Feature flags
    logLevel,
    enableAnalytics: getBooleanEnv('VITE_ENABLE_ANALYTICS', true),
    enableMonitoring: getBooleanEnv('VITE_ENABLE_MONITORING', true),
    enablePresence: getBooleanEnv('VITE_ENABLE_PRESENCE', true),
    
    // Environment info
    isDevelopment,
    isProduction,
    buildVersion: import.meta.env.VITE_BUILD_VERSION || 'unknown',
    deploymentEnvironment
  };

  // Log successful validation
  console.log('✅ Environment validation passed', {
    environment: deploymentEnvironment,
    clientId: `${azureClientId.substring(0, 8)}...`,
    apiUrl: apiBaseUrl,
    features: {
      analytics: config.enableAnalytics,
      monitoring: config.enableMonitoring,
      presence: config.enablePresence
    }
  });

  return config;
}

/**
 * Development-only function to display environment info
 */
export function displayEnvironmentInfo(): void {
  if (!import.meta.env.DEV) return;

  const config = validateEnvironment();
  
  console.group('🔧 QuickDial Environment Configuration');
  console.log('Environment:', config.deploymentEnvironment);
  console.log('Build Version:', config.buildVersion);
  console.log('Azure Client ID:', `${config.azureClientId.substring(0, 8)}...`);
  console.log('API Base URL:', config.apiBaseUrl);
  console.log('Features:', {
    Analytics: config.enableAnalytics,
    Monitoring: config.enableMonitoring,
    Presence: config.enablePresence
  });
  console.log('Log Level:', config.logLevel);
  console.groupEnd();
}

/**
 * Get environment-specific API endpoints
 */
export function getEnvironmentEndpoints(config: EnvironmentConfig) {
  return {
    api: config.apiBaseUrl,
    auth: config.azureAuthority,
    graph: 'https://graph.microsoft.com/v1.0',
    teamsAuth: `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${config.teamsPresenceClientId}`
    // NOTE: no `adminConsent` endpoint here on purpose. Admin consent must be scoped to the caller's
    // own tenant (never `/common`, which could land consent in the hosting tenant). Use
    // startAdminConsent() / getAdminConsentUrl(), which require a tenant id.
  };
}
